<template>
    <div class="sharagpt-page">
        <div class="sharagpt-sidebar" :class="{ 'sharagpt-sidebar-open': sidebarOpen }">
            <div class="d-flex justify-content-between align-items-center d-md-none mb-2">
                <strong>Conversations</strong>
                <button class="btn btn-link p-0 text-dark" @click="sidebarOpen = false" aria-label="Close">
                    <i class="bi bi-x-lg"></i>
                </button>
            </div>
            <button class="btn btn-primary btn-block mb-3" @click="newChat">
                <i class="bi bi-plus-lg mr-1"></i>New chat
            </button>

            <div v-if="loadingConversations" class="text-center py-4 text-muted">
                <div class="spinner-border spinner-border-sm" role="status"></div>
            </div>

            <div v-else class="conversation-groups">
                <div v-for="group in groupedConversations" :key="group.label" class="mb-3" v-if="group.items.length">
                    <div class="conversation-group-label">{{ group.label }}</div>
                    <div
                        v-for="c in group.items"
                        :key="c.id"
                        class="conversation-item"
                        :class="{ active: c.id === activeConversationId }"
                        @click="selectConversation(c.id)"
                    >
                        <span class="conversation-title">{{ c.title }}</span>
                        <div class="conversation-actions">
                            <i class="bi bi-pencil" @click.stop="promptRename(c)" title="Rename"></i>
                            <i class="bi bi-trash" @click.stop="confirmDelete(c)" title="Delete"></i>
                        </div>
                    </div>
                </div>
                <div v-if="!conversations.length" class="text-muted small px-2">No conversations yet.</div>
            </div>
        </div>
        <div v-if="sidebarOpen" class="sharagpt-sidebar-backdrop d-md-none" @click="sidebarOpen = false"></div>

        <div class="sharagpt-main">
            <div class="sharagpt-header d-flex justify-content-between align-items-center">
                <div class="d-flex align-items-center">
                    <button class="btn btn-link p-0 mr-2 sharagpt-menu-toggle d-md-none" @click="sidebarOpen = true" aria-label="Conversations">
                        <i class="bi bi-list"></i>
                    </button>
                    <h5 class="mb-0">SharaGPT</h5>
                </div>
                <div class="usage-indicator small text-muted" v-if="usage">
                    <span v-if="usage.free_requests_remaining > 0">
                        <i class="bi bi-stars mr-1"></i>{{ usage.free_requests_remaining }} free request{{ usage.free_requests_remaining === 1 ? '' : 's' }} remaining
                    </span>
                    <span v-else>
                        <i class="bi bi-wallet2 mr-1"></i>AI balance: KES {{ formatMoney(usage.ai_balance_kes) }}
                    </span>
                </div>
            </div>

            <!-- Empty state: no active conversation -->
            <div v-if="!activeConversationId && !messages.length" class="sharagpt-empty">
                <div class="sharagpt-empty-inner">
                    <h3>SharaGPT</h3>
                    <p class="text-muted">Your AI business assistant.</p>
                    <p class="text-muted small mb-4">Ask about your sales, inventory, products, customers, expenses and business performance.</p>

                    <div class="suggested-prompts mb-4">
                        <button
                            v-for="prompt in suggestedPrompts"
                            :key="prompt"
                            class="btn btn-outline-secondary btn-sm suggested-prompt"
                            @click="sendMessage(prompt)"
                        >
                            {{ prompt }}
                        </button>
                    </div>

                    <div class="composer composer-centered">
                        <textarea
                            v-model="draft"
                            class="form-control"
                            rows="2"
                            placeholder="Ask SharaGPT..."
                            @keydown.enter.exact.prevent="sendMessage()"
                        ></textarea>
                        <button class="btn btn-primary composer-send" :disabled="!draft.trim() || sending" @click="sendMessage()">
                            <span v-if="sending" class="spinner-border spinner-border-sm"></span>
                            <i v-else class="bi bi-send"></i>
                        </button>
                    </div>
                </div>
            </div>

            <!-- Conversation view -->
            <template v-else>
                <div class="sharagpt-messages" ref="messageList">
                    <div v-for="m in messages" :key="m.id" class="message" :class="'message-' + m.role">
                        <div class="message-bubble">{{ m.content }}</div>
                    </div>
                    <div v-if="sending" class="message message-assistant">
                        <div class="message-bubble">
                            <span class="spinner-border spinner-border-sm mr-1"></span>Thinking…
                        </div>
                    </div>
                </div>

                <div v-if="insufficientBalance" class="insufficient-balance-banner">
                    <div class="mb-2"><strong>AI balance required</strong></div>
                    <p class="mb-2 small">You've used your 2 free AI requests for this period. Add AI balance to continue using SharaGPT.</p>
                    <button class="btn btn-primary btn-sm mr-2" @click="$refs.topupModal.show()">Add AI balance</button>
                    <a href="/settings?type=integration" class="btn btn-outline-secondary btn-sm" v-if="usage && usage.byok && !usage.byok.eligible">
                        Upgrade to Pro to use your own OpenAI API key
                    </a>
                </div>

                <div class="composer composer-bottom">
                    <textarea
                        v-model="draft"
                        class="form-control"
                        rows="1"
                        placeholder="Ask SharaGPT..."
                        @keydown.enter.exact.prevent="sendMessage()"
                    ></textarea>
                    <button class="btn btn-primary composer-send" :disabled="!draft.trim() || sending" @click="sendMessage()">
                        <span v-if="sending" class="spinner-border spinner-border-sm"></span>
                        <i v-else class="bi bi-send"></i>
                    </button>
                </div>
            </template>
        </div>

        <!-- Add AI balance modal -->
        <b-modal ref="topupModal" title="Add AI balance" hide-footer centered>
            <label class="font-weight-bold">M-Pesa phone number</label>
            <input type="text" class="form-control mb-3" v-model="topupPhone" placeholder="07XXXXXXXX">
            <label class="font-weight-bold">Amount (KES)</label>
            <input type="number" min="10" class="form-control mb-3" v-model="topupAmount">
            <button class="btn btn-primary btn-block" :disabled="toppingUp || !topupPhone || !topupAmount" @click="submitTopup">
                <span v-if="toppingUp" class="spinner-border spinner-border-sm mr-1"></span>
                Send STK push
            </button>
        </b-modal>

        <!-- Delete conversation confirmation -->
        <b-modal ref="deleteModal" title="Delete conversation" hide-footer centered>
            <p>Delete "{{ pendingDelete ? pendingDelete.title : '' }}"? This can't be undone.</p>
            <div class="d-flex justify-content-end">
                <button class="btn btn-outline-secondary mr-2" @click="$refs.deleteModal.hide()">Cancel</button>
                <button class="btn btn-danger" @click="deleteConversation">Delete</button>
            </div>
        </b-modal>
    </div>
</template>

<script>
export default {
    name: 'sharagpt',
    data() {
        return {
            conversations: [],
            loadingConversations: true,
            activeConversationId: null,
            messages: [],
            draft: '',
            sending: false,
            usage: null,
            insufficientBalance: false,
            suggestedPrompts: [
                'How are my sales performing?',
                'What products are selling slowly?',
                'What should I restock?',
                'Summarise my business performance.',
            ],
            topupPhone: '',
            topupAmount: 100,
            toppingUp: false,
            pendingDelete: null,
            sidebarOpen: false,
        }
    },
    computed: {
        groupedConversations() {
            const today = []
            const yesterday = []
            const older = []
            const now = new Date()
            const isSameDay = (a, b) => a.toDateString() === b.toDateString()
            const yest = new Date(now)
            yest.setDate(now.getDate() - 1)

            this.conversations.forEach(c => {
                const d = new Date(c.created_at)
                if (isSameDay(d, now)) today.push(c)
                else if (isSameDay(d, yest)) yesterday.push(c)
                else older.push(c)
            })

            return [
                { label: 'Today', items: today },
                { label: 'Yesterday', items: yesterday },
                { label: 'Older', items: older },
            ]
        },
    },
    mounted() {
        this.loadConversations()
        this.loadUsage()

        // Deep-link support: other pages (e.g. Business Intelligence's "Ask
        // Your Business") send a question here as ?q=... instead of calling
        // an AI endpoint directly, so there is only one place that actually
        // talks to OpenAI on the frontend. Strip it from the URL immediately
        // so a page refresh never re-sends the same question.
        const params = new URLSearchParams(window.location.search)
        const deepLinkedQuestion = params.get('q')
        if (deepLinkedQuestion) {
            window.history.replaceState({}, '', '/sharagpt')
            this.sendMessage(deepLinkedQuestion)
        }
    },
    methods: {
        formatMoney(v) {
            return Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        },
        loadConversations() {
            this.loadingConversations = true
            axios.get('/api/ai/sharagpt/conversations')
                .then(res => { this.conversations = res.data })
                .catch(() => {})
                .finally(() => { this.loadingConversations = false })
        },
        loadUsage() {
            axios.get('/api/ai/usage')
                .then(res => { this.usage = res.data })
                .catch(() => {})
        },
        newChat() {
            this.activeConversationId = null
            this.messages = []
            this.draft = ''
            this.insufficientBalance = false
            this.sidebarOpen = false
        },
        selectConversation(id) {
            this.activeConversationId = id
            this.insufficientBalance = false
            this.sidebarOpen = false
            axios.get(`/api/ai/sharagpt/conversations/${id}`)
                .then(res => { this.messages = res.data.messages })
                .catch(() => {
                    this.$toasts.push({ type: 'error', message: 'Could not load conversation.' })
                })
                .finally(() => this.scrollToBottom())
        },
        promptRename(conversation) {
            const title = window.prompt('Rename conversation', conversation.title)
            if (!title || title === conversation.title) return
            axios.patch(`/api/ai/sharagpt/conversations/${conversation.id}`, { title })
                .then(() => { conversation.title = title })
                .catch(() => { this.$toasts.push({ type: 'error', message: 'Could not rename conversation.' }) })
        },
        confirmDelete(conversation) {
            this.pendingDelete = conversation
            this.$refs.deleteModal.show()
        },
        deleteConversation() {
            axios.delete(`/api/ai/sharagpt/conversations/${this.pendingDelete.id}`)
                .then(() => {
                    this.conversations = this.conversations.filter(c => c.id !== this.pendingDelete.id)
                    if (this.activeConversationId === this.pendingDelete.id) this.newChat()
                    this.$refs.deleteModal.hide()
                })
                .catch(() => { this.$toasts.push({ type: 'error', message: 'Could not delete conversation.' }) })
        },
        sendMessage(prompt) {
            const content = (prompt || this.draft).trim()
            if (!content || this.sending) return

            this.messages.push({ id: 'local-' + Date.now(), role: 'user', content })
            this.draft = ''
            this.sending = true
            this.insufficientBalance = false
            this.scrollToBottom()

            axios.post('/api/ai/sharagpt/messages', {
                conversation_id: this.activeConversationId,
                content,
            }).then(({ data }) => {
                this.activeConversationId = data.conversation_id
                this.messages.push(data.message)
                this.usage = { ...this.usage, ...data.usage }
                if (!this.conversations.find(c => c.id === data.conversation_id)) {
                    this.conversations.unshift({ id: data.conversation_id, title: data.title, created_at: new Date().toISOString() })
                }
            }).catch(err => {
                const code = err.response?.data?.error_code
                if (code === 'insufficient_balance') {
                    this.insufficientBalance = true
                } else {
                    this.$toasts.push({ type: 'error', message: err.response?.data?.message || 'SharaGPT could not complete this request.' })
                }
                // Roll back the optimistic user message on failure - sendMessage()
                // blocks re-entry while `sending` is true, so at most one
                // locally-added message can ever be pending here.
                this.messages = this.messages.filter(m => !String(m.id).startsWith('local-'))
            }).finally(() => {
                this.sending = false
                this.scrollToBottom()
            })
        },
        submitTopup() {
            this.toppingUp = true
            axios.post('/api/ai/balance/topup', { phone_number: this.topupPhone, amount: this.topupAmount })
                .then(() => {
                    this.$toasts.push({ type: 'success', message: 'Enter your M-Pesa PIN to complete the top-up.' })
                    this.$refs.topupModal.hide()
                })
                .catch(err => { this.$toasts.push({ type: 'error', message: err.response?.data?.message || 'Could not start top-up.' }) })
                .finally(() => { this.toppingUp = false })
        },
        scrollToBottom() {
            this.$nextTick(() => {
                const el = this.$refs.messageList
                if (el) el.scrollTop = el.scrollHeight
            })
        },
    },
}
</script>

<style scoped>
.sharagpt-page {
    display: flex;
    height: calc(100vh - 60px);
}
.sharagpt-sidebar {
    width: 260px;
    flex-shrink: 0;
    border-right: 1px solid rgba(0,0,0,.08);
    padding: 1rem;
    overflow-y: auto;
}
.conversation-group-label {
    font-size: .75rem;
    text-transform: uppercase;
    color: #8a8a8a;
    margin: .5rem 0 .25rem;
}
.conversation-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: .4rem .5rem;
    border-radius: .35rem;
    cursor: pointer;
    font-size: .9rem;
}
.conversation-item:hover {
    background: rgba(0,0,0,.04);
}
.conversation-item.active {
    background: rgba(0,0,0,.06);
    font-weight: 600;
}
.conversation-title {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.conversation-actions {
    display: none;
    flex-shrink: 0;
}
.conversation-item:hover .conversation-actions {
    display: flex;
    gap: .5rem;
}
.sharagpt-main {
    flex: 1;
    display: flex;
    flex-direction: column;
    padding: 1rem 1.5rem;
    min-width: 0;
}
.sharagpt-empty {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
}
.sharagpt-empty-inner {
    max-width: 560px;
    width: 100%;
    text-align: center;
}
.suggested-prompts {
    display: flex;
    flex-wrap: wrap;
    gap: .5rem;
    justify-content: center;
}
.composer {
    display: flex;
    gap: .5rem;
    align-items: flex-end;
}
.composer-centered {
    max-width: 560px;
    margin: 0 auto;
}
.composer-bottom {
    padding-top: .75rem;
}
.composer-send {
    flex-shrink: 0;
}
.sharagpt-messages {
    flex: 1;
    overflow-y: auto;
    padding: 1rem 0;
}
.message {
    display: flex;
    margin-bottom: .75rem;
}
.message-user {
    justify-content: flex-end;
}
.message-bubble {
    max-width: 70%;
    padding: .6rem .9rem;
    border-radius: .75rem;
    white-space: pre-wrap;
}
.message-user .message-bubble {
    background: var(--primary, #fd4719);
    color: #fff;
}
.message-assistant .message-bubble {
    background: rgba(0,0,0,.05);
}
.insufficient-balance-banner {
    border: 1px solid rgba(0,0,0,.08);
    border-radius: .5rem;
    padding: 1rem;
    margin-top: .5rem;
}

/* Mobile: sidebar becomes an off-canvas drawer instead of a permanent column */
@media (max-width: 768px) {
    .sharagpt-page {
        position: relative;
        overflow: hidden;
    }
    .sharagpt-sidebar {
        position: fixed;
        top: 0;
        bottom: 0;
        left: 0;
        width: 82%;
        max-width: 300px;
        background: #fff;
        z-index: 1050;
        transform: translateX(-100%);
        transition: transform .2s ease-in-out;
        box-shadow: 2px 0 10px rgba(0,0,0,.15);
    }
    .sharagpt-sidebar-open {
        transform: translateX(0);
    }
    .sharagpt-sidebar-backdrop {
        position: fixed;
        inset: 0;
        background: rgba(0,0,0,.4);
        z-index: 1040;
    }
    .sharagpt-main {
        padding: .75rem 1rem;
        width: 100%;
    }
    .suggested-prompts {
        flex-direction: column;
        align-items: stretch;
    }
    .suggested-prompt {
        white-space: normal;
        text-align: left;
    }
    .message-bubble {
        max-width: 88%;
    }
    .usage-indicator {
        text-align: right;
        max-width: 45%;
    }
}
</style>
